import React from 'react';
import { useT } from '../i18n';
import type { BonusView } from '../lib/bonus-state';
import { BONUS_AVAILABLE_IN_BUILD } from '../lib/bonus-availability';

const GOLD = 'var(--gold)';

export interface BonusPromoBannerProps {
    view: BonusView;
    /** Already-translated CTA label — the HOST owns action-honesty (connect vs deposit copy). */
    primaryLabel: string;
    /** Connect flow when disconnected, deposit modal when connected. Supplied by the host. */
    onPrimary: () => void;
    termsHref?: string;
}

/**
 * CF-BONUS-VISIBILITY-V2 — the promotional (main-screen, above-the-fold) surface for the
 * first-deposit offer, mounted for demo/disconnected AND connected/no-deposit players.
 *
 * Rendering rule is IDENTICAL to BonusTeaser (BonusPanel.tsx): null unless the build
 * participates AND the server confirmed `none` + `offer_available`. Nothing is inferred
 * client-side; `unavailable` (including the NETWORK_UNAVAILABLE default) renders nothing,
 * so an un-updated caller advertises nothing. Seeing the banner reserves nothing — the
 * qualification line and the terms link carry that, verbatim from the approved teaser copy.
 *
 * No new copy: headline/body/availability/terms strings are the shipped `bonus.teaser.*`
 * keys; the CTA label is a shipped `coinflip.*` key chosen by the host per connection
 * state (Scorer V2 ruling 2026-08-10 0342, amendment 2). Presentation-only component.
 */
export function BonusPromoBanner({ view, primaryLabel, onPrimary, termsHref }: BonusPromoBannerProps) {
    const t = useT();
    if (!BONUS_AVAILABLE_IN_BUILD) return null;
    if (view.kind !== 'none' || !view.offerAvailable) return null;
    // CF-BONUS-WOW-V1 restyle (PL #12717 "completely blending in"): the INVERTED card —
    // filled gold ground with dark text, the only such card in the app, so it cannot read
    // as furniture. Structure, props, predicate and strings are UNCHANGED from V2; the
    // diff is presentation only. CTA is dark-on-gold (a gold button on a gold ground
    // would vanish). Retains the shipped literal `0 0 28px rgba(212,175,55,0.28)` in the
    // outer glow so the served-bytes probe lineage stays meaningful.
    return (
        <div className="not-prose my-3 rounded-2xl p-4 text-center"
             style={{
                 background: 'linear-gradient(160deg, #f7dd7f 0%, #e8c24f 55%, #d4af37 100%)',
                 border: '1px solid rgba(244,213,111,0.9)',
                 boxShadow: '0 0 28px rgba(212,175,55,0.28), 0 0 46px rgba(212,175,55,0.30)',
             }}>
            <p className="text-[18px] font-extrabold leading-snug" style={{ color: '#1a1408' }}>
                {t('bonus.teaser.headline')}
            </p>
            <p className="text-[12px] mt-1.5" style={{ color: '#3a2f10' }}>
                {t('bonus.teaser.body')}
            </p>
            <button
                onClick={onPrimary}
                className="w-full py-3 rounded-xl text-[14px] font-extrabold mt-3 active:scale-95 transition-transform"
                style={{ background: '#14100a', color: GOLD }}
            >
                {primaryLabel}
            </button>
            <p className="text-[10px] mt-2" style={{ color: '#4a3d16' }}>
                {t('bonus.teaser.availability')}
            </p>
            {termsHref && (
                <a
                    href={termsHref}
                    style={{ color: '#241c06', fontSize: 12, textDecoration: 'underline', display: 'inline-block', marginTop: 4 }}
                >
                    {t('bonus.teaser.terms_link')} →
                </a>
            )}
        </div>
    );
}
